import { Platform } from 'react-native';
import jwtDecode from 'jwt-decode';
import {
  ensureVirtualTerminal,
  type VirtualTerminalAction,
} from '@oolio-group/pay-embedded';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { getItem, removeItem, setItem } from '../storage/interface';
import { logCdsEvent } from '../actionLogging/cdsAudit';

export const TAP2PAY_VIRTUAL_TERMINAL_KEY = 'TAP2PAY_VIRTUAL_TERMINAL';

export interface VirtualTerminalOwner {
  deviceId: string;
  merchant: string;
  store: string;
}

export interface StoredVirtualTerminal extends VirtualTerminalOwner {
  terminalId: string;
  serialNumber?: string;
  action?: VirtualTerminalAction;
  savedAt: string;
}

export type VirtualTerminalTrigger = 'setup' | 'diagnosis' | 'payment';

export interface EnsureVirtualTerminalConfig extends VirtualTerminalOwner {
  accessToken: string;
  userId: string;
  trigger: VirtualTerminalTrigger;
  force?: boolean;
}

const inFlight = new Map<string, Promise<StoredVirtualTerminal>>();
let clearGeneration = 0;
const loggedSkips = new Set<string>();

const ownerKey = (owner: VirtualTerminalOwner): string =>
  `${owner.deviceId}|${owner.merchant}|${owner.store}`;

export const userIdFromToken = (token?: string): string => {
  if (!token) return '';
  try {
    return jwtDecode<{ sub?: string }>(token).sub ?? '';
  } catch {
    return '';
  }
};

export const getStoredVirtualTerminal = async (
  owner?: Partial<VirtualTerminalOwner>,
): Promise<StoredVirtualTerminal | undefined> => {
  if (Platform.OS !== 'android') return undefined;
  const saved = await getItem<StoredVirtualTerminal>(
    TAP2PAY_VIRTUAL_TERMINAL_KEY,
  );
  if (!saved?.terminalId) return undefined;
  if (!owner) return saved;
  const matches =
    saved.deviceId === owner.deviceId &&
    saved.merchant === owner.merchant &&
    saved.store === owner.store;
  return matches ? saved : undefined;
};

export const ensureDeviceVirtualTerminal = async (
  config: EnsureVirtualTerminalConfig,
): Promise<StoredVirtualTerminal> => {
  const key = ownerKey(config);
  if (!config.force) {
    const saved = await getStoredVirtualTerminal(config).catch(() => undefined);
    if (saved) return saved;
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  const generation = clearGeneration;
  const request = {
    merchant: config.merchant,
    store: config.store,
    userId: config.userId,
    deviceIdentifier: config.deviceId,
  };
  const tracked: Promise<StoredVirtualTerminal> = (async () => {
    let response: Awaited<ReturnType<typeof ensureVirtualTerminal>> | undefined;
    try {
      const result = await ensureVirtualTerminal({
        ...request,
        accessToken: config.accessToken,
      });
      response = result;
      if (!result.success || !result.terminalId) {
        throw new Error(
          result.errorMessage || 'Virtual terminal was not returned',
        );
      }
      const stored: StoredVirtualTerminal = {
        deviceId: config.deviceId,
        merchant: config.merchant,
        store: config.store,
        terminalId: result.terminalId,
        serialNumber: result.serialNumber,
        action: result.action,
        savedAt: new Date().toISOString(),
      };
      if (generation === clearGeneration) {
        await setItem(TAP2PAY_VIRTUAL_TERMINAL_KEY, stored);
      }
      return stored;
    } catch (error) {
      logCdsEvent({
        actionType: 'T2P_VIRTUAL_TERMINAL_FAILED',
        tag: AuditLogTags.PAYMENT,
        phrase: 'virtual terminal creation failed',
        level: 'error',
        context: {
          deviceId: config.deviceId,
          merchant: config.merchant,
          store: config.store,
          trigger: config.trigger,
          request: { ...request, hasAccessToken: Boolean(config.accessToken) },
          response,
          httpStatus: response?.httpStatus,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
      throw error;
    }
  })().finally(() => {
    if (inFlight.get(key) === tracked) inFlight.delete(key);
  });

  if (!config.force) inFlight.set(key, tracked);
  return tracked;
};

export const logVirtualTerminalSkipped = (
  deviceId: string,
  missing: string[],
): void => {
  const key = `${deviceId}|${missing.join(',')}`;
  if (loggedSkips.has(key)) return;
  loggedSkips.add(key);
  logCdsEvent({
    actionType: 'T2P_VIRTUAL_TERMINAL_FAILED',
    tag: AuditLogTags.PAYMENT,
    phrase: 'virtual terminal creation skipped',
    level: 'warn',
    context: { deviceId, trigger: 'setup', missing },
  });
};

export const clearStoredVirtualTerminal = async (): Promise<void> => {
  clearGeneration += 1;
  inFlight.clear();
  await removeItem(TAP2PAY_VIRTUAL_TERMINAL_KEY);
};
